import React from 'react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { RecorderError } from './useRecorder'
import './micBlockedNotice.css'

type Browser = 'ios' | 'android' | 'safari' | 'firefox' | 'edge' | 'chrome' | 'other'

const detectBrowser = (): Browser => {
  if (typeof navigator === 'undefined') return 'other'
  const ua = navigator.userAgent || ''
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1)
  if (isIOS) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  if (/Firefox\//.test(ua)) return 'firefox'
  if (/Edg\//.test(ua)) return 'edge'
  if (/Chrome\//.test(ua)) return 'chrome'
  if (/Safari\//.test(ua)) return 'safari'
  return 'other'
}

/** The fix for the learner's own browser, in its own words. */
export const micFixSteps = (browser: Browser = detectBrowser()): string => {
  switch (browser) {
    case 'ios':
      return i18n.t('writeSpeak:micBlocked.fix.ios')
    case 'android':
      return i18n.t('writeSpeak:micBlocked.fix.android')
    case 'safari':
      return i18n.t('writeSpeak:micBlocked.fix.safari')
    case 'firefox':
      return i18n.t('writeSpeak:micBlocked.fix.firefox')
    case 'edge':
    case 'chrome':
      return i18n.t('writeSpeak:micBlocked.fix.chromium')
    default:
      return i18n.t('writeSpeak:micBlocked.fix.other')
  }
}

interface MicBlockedNoticeProps {
  error: RecorderError
  onRetry?: () => void
}

/** Replaces the old alert(): says what happened and how to fix it in this browser. */
const MicBlockedNotice: React.FC<MicBlockedNoticeProps> = ({ error, onRetry }) => {
  const { t } = useTranslation('writeSpeak')
  if (!error) return null
  const title =
    error === 'blocked'
      ? t('micBlocked.blockedTitle')
      : error === 'no-device'
        ? t('micBlocked.noDeviceTitle')
        : t('micBlocked.unsupportedTitle')
  const body =
    error === 'blocked'
      ? micFixSteps()
      : error === 'no-device'
        ? t('micBlocked.noDeviceBody')
        : t('micBlocked.unsupportedBody')
  return (
    <div className='mic-blocked' role='alert'>
      <span className='mic-blocked__icon' aria-hidden='true'>
        <svg
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <rect x='9' y='3' width='6' height='11' rx='3' />
          <path d='M5 11a7 7 0 0 0 14 0M12 18v3M4 4l16 16' />
        </svg>
      </span>
      <div className='mic-blocked__copy'>
        <b>{title}</b>
        <span>{body}</span>
      </div>
      {onRetry && error !== 'unsupported' && (
        <button type='button' className='mic-blocked__retry' onClick={onRetry}>
          {t('micBlocked.retry')}
        </button>
      )}
    </div>
  )
}

export default MicBlockedNotice
